import * as THREE from 'three';
import type { CameraPreset } from '../types/threeStudio';

/**
 * Resolves an orbit-style camera preset against the loaded model's real bounds.
 *
 * The alternatives both fail: a distance derived from viewport width alone does
 * not re-frame when the subject changes size (so the pickup ends up cropped), and
 * a hand-tuned position frames exactly one car.
 */

/** Breathing room around the silhouette. */
export const CAMERA_FIT_MARGIN = 1.14;
/** Extra padding so the turntable and contact shadows stay inside the frame. */
export const FRAME_PADDING = 1.15;
/**
 * How much of the subject's own length to add to the fitted distance. A full
 * depth is far more than needed and pushes the car into the middle of the
 * frame; the remainder only has to cover the foreshortening at an angle.
 */
export const DEPTH_WEIGHT = 0.55;

export function computeFitDistance(
  size: THREE.Vector3,
  fovDegrees: number,
  aspect: number
): number {
  const verticalFov = (fovDegrees * Math.PI) / 180;
  const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * aspect);

  const width = Math.max(0.5, size.x) * FRAME_PADDING;
  const height = Math.max(0.5, size.y) * FRAME_PADDING;
  const depth = Math.max(0.1, size.z);

  // Solve both axes and take the larger, then add a share of the subject's own
  // depth so the near end of a long car is not clipped.
  return (
    Math.max(
      width / 2 / Math.tan(horizontalFov / 2),
      height / 2 / Math.tan(verticalFov / 2)
    ) + depth * DEPTH_WEIGHT
  ) * CAMERA_FIT_MARGIN;
}

export interface CameraPose {
  position: [number, number, number];
  target: [number, number, number];
}

export function solveCameraPose(
  preset: CameraPreset,
  bounds: THREE.Box3,
  fovDegrees: number,
  aspect: number
): CameraPose {
  const size = bounds.getSize(new THREE.Vector3());
  const centre = bounds.getCenter(new THREE.Vector3());

  const fitted = computeFitDistance(size, fovDegrees, aspect) * preset.distanceFactor;
  const azimuth = (preset.azimuthDegrees * Math.PI) / 180;
  const elevation = (preset.elevationDegrees * Math.PI) / 180;

  const target: [number, number, number] = [
    centre.x,
    centre.y + size.y * preset.targetHeightFactor,
    centre.z,
  ];

  // Horizontal component of the orbit, so a low elevation still circles the car
  // instead of collapsing onto the vertical axis.
  const horizontal = Math.cos(elevation) * fitted;

  return {
    position: [
      centre.x + Math.sin(azimuth) * horizontal,
      centre.y + Math.sin(elevation) * fitted + size.y * preset.targetHeightFactor,
      centre.z + Math.cos(azimuth) * horizontal,
    ],
    target,
  };
}